import React, { useEffect, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Collapse,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import HistoryIcon from '@mui/icons-material/History'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import { makeGetRequest } from 'utils/api'
import urls from 'utils/urls'

/**
 * RecommandationsHistoryModal — Modal centrée listant l'historique des
 * recommandations patrimoniales générées par le Life Agent.
 *
 * LAP-CP02 — Source de vérité côté backend : table `displayed_advice`
 * (un row par conseil affiché à l'utilisateur, scopé par user_id, avec
 * created_at, content, source). L'endpoint à exposer côté API :
 *
 *   GET /api/general/mon-bilan-patrimonial-reports/?history=true
 *     → renvoie un array d'objets { id, content, source, created_at }
 *       triés du plus récent au plus ancien.
 *
 * Tant que cet endpoint n'existe pas, on bascule sur un mock local (clairement
 * marqué TODO ci-dessous) pour que l'UI soit validable tout de suite. Quand
 * l'endpoint sera prêt, la transition est transparente : suffit de supprimer
 * le bloc buildMockHistory et le catch.
 */
function RecommandationsHistoryModal({ open, onClose, currentReport }) {
  const { t, i18n } = useTranslation('layout')
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(null)

  const lang = (i18n.language || 'fr').split('-')[0]

  useEffect(() => {
    if (!open) return

    const fetchHistory = async () => {
      setLoading(true)
      setError(null)
      try {
        const { data } = await makeGetRequest(
          `${urls.MonBilanPatrimonialReports}?history=true`,
          {},
          true,
        )
        // LAP-CP05 — Le backend peut renvoyer plusieurs structures différentes :
        //   - { history: [...] }
        //   - [...] (array de mixed types comme le endpoint principal)
        //   - { data: [...] }
        //   - { conseils_history: [...] }
        // Le endpoint principal renvoie un array de reports de types divers
        // (portrait, patrimoine, conseils, etc.) ; il faut filtrer pour ne
        // garder que les entrées qui ont un contenu de conseil exploitable.
        const list = Array.isArray(data)
          ? data
          : data?.history || data?.data || data?.conseils_history || []

        const normalised = list
          .map(normaliseEntry)
          .filter((e) => e.content && String(e.content).trim().length > 0)

        if (normalised.length === 0) {
          // Endpoint répond mais sans conseil exploitable → fallback mock pour
          // que l'utilisateur visualise l'expérience cible
          setHistory(buildMockHistory(currentReport, lang))
          setError('mock')
        } else {
          // Si le conseil courant n'est pas dans la liste (cas où l'API ne
          // renvoie que l'historique passé), on l'ajoute en tête
          const hasCurrent = currentReport?.content
            ? normalised.some((e) => e.content === currentReport.content)
            : false
          const finalList =
            hasCurrent || !currentReport?.content
              ? normalised
              : [normaliseEntry(currentReport), ...normalised]
          setHistory(finalList)
        }
      } catch (e) {
        // LAP-CP02 — TODO BACKEND : retirer le fallback mock une fois que
        // l'endpoint /mon-bilan-patrimonial-reports/?history=true est dispo.
        console.warn(
          '[ConseilsCard] /history=true endpoint pas dispo, fallback mock',
          e?.message,
        )
        setHistory(buildMockHistory(currentReport, lang))
        setError('mock')
      } finally {
        setLoading(false)
      }
    }
    fetchHistory()
  }, [open, currentReport, lang])

  const formatDate = (d) => {
    if (!d) return ''
    try {
      const dj = dayjs(d).locale(lang === 'fr' ? 'fr' : 'en')
      return dj.format(lang === 'fr' ? 'D MMMM YYYY · HH[h]mm' : 'MMMM D, YYYY · HH:mm')
    } catch {
      return String(d)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: '#0e1a36',
          border: '1px solid rgba(155,140,255,0.25)',
          borderRadius: 3,
          color: 'white',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          py: 2,
        }}
      >
        <HistoryIcon sx={{ color: '#9b8cff' }} />
        <Box flex={1}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {t('monPatrimoine.historyTitle', {
              defaultValue: 'Historique des recommandations',
            })}
          </Typography>
          <Typography variant="caption" sx={{ color: '#a0b4d6' }}>
            {t('monPatrimoine.historySubtitle', {
              defaultValue: 'Toutes les recommandations générées par votre Life Agent',
            })}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#a0b4d6' }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, maxHeight: 520 }}>
        {loading ? (
          <Box
            sx={{
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CircularProgress size={32} sx={{ color: '#9b8cff' }} />
            <Typography variant="body2" sx={{ color: '#a0b4d6' }}>
              {t('monPatrimoine.historyLoading', {
                defaultValue: 'Chargement de l\'historique…',
              })}
            </Typography>
          </Box>
        ) : history.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#a0b4d6' }}>
              {t('monPatrimoine.historyEmpty', {
                defaultValue: 'Aucune recommandation dans l\'historique pour le moment.',
              })}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {error === 'mock' && (
              <Box
                sx={{
                  p: 1.5,
                  mb: 1,
                  bgcolor: 'rgba(251,191,36,0.10)',
                  border: '1px solid rgba(251,191,36,0.3)',
                  borderRadius: 1.5,
                }}
              >
                <Typography variant="caption" sx={{ color: '#fbbf24' }}>
                  {t('monPatrimoine.historyMockNotice', {
                    defaultValue:
                      'Données de démonstration · l\'endpoint historique backend sera bientôt disponible',
                  })}
                </Typography>
              </Box>
            )}
            {history.map((entry, idx) => {
              const isOpen = expanded === entry.id
              const isCurrent = idx === 0
              return (
                <Box
                  key={entry.id || idx}
                  sx={{
                    bgcolor: isCurrent
                      ? 'rgba(155,140,255,0.10)'
                      : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${
                      isCurrent ? 'rgba(155,140,255,0.30)' : 'rgba(255,255,255,0.06)'
                    }`,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    onClick={() => setExpanded(isOpen ? null : entry.id)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                    }}
                  >
                    <AutoAwesomeIcon sx={{ color: '#9b8cff', fontSize: 18 }} />
                    <Box flex={1} minWidth={0}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#dbe5f7',
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {previewContent(entry.content)}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Typography variant="caption" sx={{ color: '#a0b4d6' }}>
                          {formatDate(entry.date)}
                        </Typography>
                        {isCurrent && (
                          <Chip
                            label={t('monPatrimoine.historyCurrentBadge', {
                              defaultValue: 'En cours',
                            })}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(155,140,255,0.25)',
                              color: '#d4ccff',
                              fontWeight: 600,
                              height: 18,
                              fontSize: 10,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    {isOpen ? (
                      <ExpandLessIcon sx={{ color: '#a0b4d6' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: '#a0b4d6' }} />
                    )}
                  </Box>
                  <Collapse in={isOpen}>
                    <Box
                      sx={{
                        px: 2.5,
                        py: 2,
                        borderTop: '1px solid rgba(255,255,255,0.06)',
                        bgcolor: 'rgba(0,0,0,0.15)',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#dbe5f7',
                          fontStyle: 'italic',
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {entry.content}
                      </Typography>
                      {entry.source && (
                        <Box
                          mt={1.5}
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          justifyContent="flex-end"
                        >
                          <AutoAwesomeIcon sx={{ fontSize: 14, color: '#9b8cff' }} />
                          <Typography variant="caption" sx={{ color: '#9b8cff' }}>
                            {entry.source}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

function previewContent(text) {
  if (!text) return ''
  const oneLine = String(text).replace(/\s+/g, ' ').trim()
  return oneLine.length > 90 ? oneLine.slice(0, 90) + '…' : oneLine
}

function normaliseEntry(raw) {
  if (!raw) return { id: Math.random().toString(36), content: '', source: 'Life Agent', date: null }

  // LAP-CP05 — Le main endpoint enveloppe parfois dans { type, data: {...} }
  // (cf. MonPatrimoine/MainBoard.js : val.data passé en oldData). On déballe
  // si besoin pour récupérer le vrai payload.
  const data = raw.data || raw.advice || raw.displayed_advice || raw.payload || raw

  return {
    id:
      data.id ||
      raw.id ||
      data.uuid ||
      raw.uuid ||
      data.created_at ||
      raw.created_at ||
      Math.random().toString(36),
    content:
      data.content ||
      data.text ||
      data.advice ||
      data.message ||
      data.recommendation ||
      raw.content ||
      raw.text ||
      '',
    source: data.source || raw.source || 'Life Agent',
    date:
      data.created_at ||
      data.createdAt ||
      data.date ||
      data.generated_at ||
      data.timestamp ||
      raw.created_at ||
      raw.createdAt ||
      raw.date,
  }
}

// LAP-CP02 — TODO BACKEND : à supprimer une fois l'endpoint history dispo
function buildMockHistory(currentReport, lang) {
  const now = new Date()
  const daysAgo = (n) => new Date(now.getTime() - n * 24 * 3600 * 1000).toISOString()
  const items = []
  if (currentReport) {
    items.push(
      normaliseEntry({
        ...currentReport,
        id: 'current',
        date: currentReport.date || currentReport.created_at || now.toISOString(),
      }),
    )
  }
  const samples = {
    fr: [
      'Constituer une épargne de précaution de 3 à 6 mois de dépenses sur Livret A avant tout investissement long terme.',
      'Ouvrir un PER pour bénéficier de la déductibilité fiscale à votre TMI actuelle de 30%.',
      'Diversifier votre patrimoine en sortant du 100% compte courant : commencez par un ETF Monde via PEA.',
      'Étudier l\'achat de votre résidence principale à Strasbourg, votre capacité d\'emprunt est de ~280 000€.',
    ],
    en: [
      'Build an emergency fund of 3 to 6 months of expenses on a Livret A before any long-term investment.',
      'Open a PER to benefit from tax deductibility at your current 30% marginal rate.',
      'Diversify your wealth out of the 100% current account: start with a World ETF via PEA.',
      'Consider buying your primary residence in Strasbourg, your borrowing capacity is ~€280k.',
    ],
  }
  const list = samples[lang] || samples.fr
  list.forEach((content, idx) => {
    items.push(
      normaliseEntry({
        id: `mock-${idx}`,
        content,
        source: 'Life Agent',
        date: daysAgo((idx + 1) * 7),
      }),
    )
  })
  return items
}

export default RecommandationsHistoryModal
